const content = {
  component: 'PageTypeA',
  cssClass: 'page-two-a',
  meta: {
    title: 'Teaching tool could soon accelerate drug research',
    description: `CERN is producing increasing amounts of data—and they need an effective solution to store it.`,
    og: {
      type: 'website',
      title: 'Cloud is improving science—Learn how',
      description: `Virtual Reality software could improve scientific research & speed up drug discovery.`,
      image: require('../../assets/images/social/og_page2-A.jpg')
    },
    twitter: {
      card: 'summary',
      title: 'Discover how combining cloud and VR could transform drug research',
      description: `Nano SimBox is a VR-based software by Interactive Scientific that uses the power of the cloud to assist in molecular science, potentially speeding up drug discoveries.`,
      site: '@Oracle',
      creator: '@Oracle',
      image: require('../../assets/images/social/twitter_page2-A.jpg')
    }
  },
  title: 'Teaching tool could soon accelerate drug research',
  heroImage: {
    preload: require('../../assets/images/backgrounds/p2a/page-two_a-pre.jpg'),
    loaded: require('../../assets/images/backgrounds/p2a/page-two_a@2x.jpg')
  },
  sections: [
    { 
      component: 'SectionQuote', 
      content: {
        quote: `Even within a group of researchers working on a drug, everyone’s coming from different approaches and backgrounds. It’s about getting those people on the same page.`,
        author: `Beck Page`,
        position: `CEO, Interactive Scientific`,
        type: 'quote'
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
		Harnessing computing power delivered online, Bristol, U.K. startup Interactive Scientific has developed virtual reality software that depicts 3-D properties of more than 100 molecules, letting students and scientists thread, knot, and wind molecular structures that adhere to the laws of physics. The software, called NanoSimBox, could eventually speed up drug discovery, simplify scientific presentations, and let pharmaceutical sales reps demonstrate drugs’ properties to doctors, CEO Becky Sage says.
        `,
        type: 'intro'
      }
    },
    { 
      component: 'SectionSeperator', 
      content: {
        image: null
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
          <p>“The virtual reality lets a team get around a molecule and interrogate a drug target,” Sage says. “We always knew we needed to run on the cloud in order to scale. We need to be able to run those simulations in a way that feels seamless and is remote.”  The software, which users interact with via a VR headset, is being used in more than 40 schools in the U.K., Germany, and China to teach chemistry in a way that’s more immersive than traditional plastic models nor flat representations on a screen.</p>
          <p>Next up for the startup: cracking commercial clients where the tool could let teams of pharmaceutical researchers study potential targets for drugs. Another commercial application could be pharmaceutical company meetings, replacing slides and hand-written notes shown for drug research approval. VR modelling could help, says Sage.</p>
        `,
        type: 'default'
      }
    },
    { 
      component: 'SectionImage', 
      content: {
        image: require('../../assets/images/content/p2a/page-two-a_a@2x.jpg'),
        alt: '',
        type: 'image'
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
          <p>The software’s real-time choreography of molecular movements and interactions are calculated by supercomputing-scale processing power served from Oracle’s Cloud Infrastructure, which handles computations, storage, and distribution of the work across remote machines.</p>
          <p>Running on Oracle supercomputers in the cloud featuring Nvidia Tesla GPUs lets researchers add power as they need and “interact using human intuition” about how drugs would bind onto a protein, says Oracle cloud innovation architect Phil Bates. Phil worked with Interactive Scientific and Bristol University researchers on the simulations described in a recent scientific paper, and subsequent depictions of large molecules with hundreds of thousands of atoms, such as proteins.</p>
        `,
        type: 'default'
      }
    },
    { 
      component: 'SectionSeperator', 
      content: {
        image: require('../../assets/images/svg/microscope.svg')
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
          <p>Even untrained users are able to catch on quickly. In a recent paper in the journal Science Advances, Becky Sage and her collaborators, including Interactive Scientific chief technology officer Phillip Tew and company advisor Adrian Mulholland, a chemistry professor at Bristol University, showed that their software lets users manipulate proteins much faster in virtual reality than by using a touchscreen or a mouse. In the study, users threaded carbon nanotubes, knotted a protein molecule, and performed other tasks that replicated contemporary research.</p>
          <p>NanoSimBox works with standard VR setups for tablets and smartphones and the company is building desktop versions, says Phillip Tew.</p>
        `,
        type: 'default'
      }
    },
    { 
      component: 'SectionQuote', 
      content: {
        quote: `This is not just some thin model&mdash;this is research-grade simulation.`,
        author: `Phil Tew`,
        position: `CEO, Bristol University`,
        type: 'quote',
        backgroundImage: require('../../assets/images/content/p2a/page-two-a_quote@2x.jpg')
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
          <p>Interactive Scientific is also talking to the energy sector about applying its software to molecular design. The technology could also let medical researchers in far-flung locations interrogate molecules together to study exotic materials such as cancer-fighting substances from sea sponges, says chemistry professor Adrian Mulholland. “It’s a change in the way we do science,” he says. Read more here about how the world feels about technology in our Delivering on the promise of Digital Innovation report  or trial out our latest autonomous solutions.</p>`,
        type: 'default'
      }
    },
    { 
      component: 'SectionCallToAction', 
      content: {
        title: `Transform your business with Oracle`,
        ctas: [
          { 
            image: require('../../assets/images/svg/cloud-housing.svg'),
            copy: 'Read our Delivering on the promise of Digital Innovation report',
            // url: 'https://www.oracle.com/uk/area-of-interest/it/tech-to-transform-your-business.html?source=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_AW_HI_CM_UK_C11_Q22_WP&intcmp=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_AW_HI_CM_UK_C11_Q22_WP',
            url: 'https://www.oracle.com/uk/area-of-interest/it/tech-to-transform-your-business.html?source=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_AW_HI_CM_UK_C11_Q22_WP&intcmp=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_AW_HI_CM_UK_C11_Q22_WP',
            buttonCopy: 'Start your trial'
          },
          { 
            image: require('../../assets/images/svg/piechart.svg'),
            copy: 'Trial out our latest autonomous solutions',
            // url: 'https://cloud.oracle.com/datawarehouse?source=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_CT_TR_CM_UK_C18_Q24_DW&intcmp=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_CT_TR_CM_UK_C18_Q24_DW',
            url: 'https://cloud.oracle.com/datawarehouse?source=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_CT_TR_CM_UK_C18_Q24_DW&intcmp=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_CT_TR_CM_UK_C18_Q24_DW',
            buttonCopy: 'Read report'
          }
        ],
        type: 'cta'
      }
    }
  ]



}

export default content;